import React, { forwardRef, useImperativeHandle, useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { Tooltip } from 'react-tooltip'

import styles from './styles.module.scss'

import { getCurrencies, getCustomers, getPlaces } from '../../../api'

import Input from '../../Core/Input/Input'
import Label from '../../Core/InputLabel'
import InputSelect from '../../Core/InputSelect'
import Checkbox from '../../Core/Checkbox/Checkbox2'
import InputSelectMultiple from '../../Core/InputSelectMultiple'

const dateInputFormat = 'YYYY-MM-DD'

const InvoiceForm = forwardRef(({companyId, showAssignToProject, onChange}, ref) => {
  const { t } = useTranslation()

  const termsOptions = [{id: 7, title: t('{{count}} days', {count: 7})}, {id: 10, title: t('{{count}} days', {count: 10})}, {id: 14, title: t('{{count}} days', {count: 14})}, {id: 21, title: t('{{count}} days', {count: 21})}, {id: 30, title: t('{{count}} days', {count: 30})}]

  const [{currencies, customers, places}, setOptions] = useState({
    currencies: [],
    customers: [],
    places: [],
  })

  const [form, setForm] = useState({
    invoice_number: '',
    customer_id: '',
    place_ids: [],
    invoice_date: '',
    due_date: '',
    notes: '',
    internal_notes: '',
    payment_terms: '',
    currency: '',
    language: 'lt',
    default_vat: '0',
    auto_assign_to_project: false,
  })

  const [errors, setErrors] = useState({})
  const [showHelper, setShowHelper] = useState(false)

  const tooltipRef = useRef(null)

  useImperativeHandle(ref, () => ({
    getForm: () => {
      return {
        ...form,
        invoice_date: form.invoice_date ? moment(form.invoice_date).format('YYYY-MM-DD') : null,
        due_date: form.due_date ? moment(form.due_date).format('YYYY-MM-DD') : null,
        payment_terms: form.payment_terms ? form.payment_terms.toString() : null,
        ...(form.place_ids.length ? {places: places.filter(p => form.place_ids.includes(p.id))} : {}),
        ...(form.customer_id ? {customer: customers.find(c => c.id === form.customer_id)} : {}),
      }
    },
    setForm: data => {
      setForm(prev => ({
        ...prev,
        ...data,
      }))
    },
    validateForm: () => validateForm(),
    showHelper: () => {
      tooltipRef.current.open({
        content: t('Choose at least one place'),
      })
      setShowHelper(true)
      setTimeout(() => {
        tooltipRef.current.close()
        setShowHelper(false)
      }, 3000)
    },
    setErrors: (data) => setErrors(data)
  }))

  useEffect(() => {
    init()
  }, [companyId])

  const init = async () => {
    const [currencies, customers, places] = await Promise.all([
      getCurrencies(companyId),
      getCustomers(companyId),
      getPlaces(companyId),
    ])

    if (Array.isArray(currencies) && Array.isArray(customers) && Array.isArray(places)) {
      setOptions({
        currencies: currencies.map(curr => ({id: curr.code, name: `${curr.code} ${curr.name ? `(${curr.name})` : ''}`})),
        customers: customers.map(customer => ({...customer, name: customer.display_name})),
        places: [{id: 0, name: t('No place')}, ...places],
      })
    }
  }

  const handleChangePlaces = res => {
    setForm(prev => ({
      ...prev,
      place_ids: res,
    }))
    onChange({place_ids: res})
  }

  const handleChangeForm = (e) => {
    const { name, value, type, checked } = e.target
    const inputValue = type === 'checkbox' ? checked : value

    if (name === 'payment_terms') {
      setForm(prev => ({
        ...prev,
        [name]: inputValue,
        due_date: form.invoice_date ? moment(form.invoice_date).add(inputValue, 'days').format('YYYY-MM-DD') : form.due_date,
      }))
      return
    }

    setForm(prev => ({
      ...prev,
      [name]: inputValue
    }))

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: false
      }))
    }

    if (name === 'default_vat' || name === 'invoice_number' || name === 'currency') {
      onChange({[name]: inputValue})
    }
  }

  const validateForm = () => {
    const nextErrors = {
      customer_id: !form.customer_id,
      place_ids: !form.place_ids.length,
      invoice_number: !String(form.invoice_number || '').trim(),
      invoice_date: !form.invoice_date,
    }
    setErrors(nextErrors)
    return !Object.values(nextErrors).some(Boolean)
  }

  return (
    <div className={styles.form}>
      <div className={styles.formRow}>
        <div className={styles.formItem}>
          <Label text={<>{t('Customer')}<span className={styles.requiredMark}>*</span></>} htmlFor='customer_id' />
          <InputSelect
            id='customer_id'
            placeholder={t('Select customer')}
            options={customers}
            value={form.customer_id}
            name='customer_id'
            onChange={handleChangeForm}
            valueKey='id'
            labelKey='name'
            error={errors.customer_id}
          />
        </div>
        <div className={styles.formItem}>
          <Label text={<>{t('Place')}<span className={styles.requiredMark}>*</span></>} htmlFor='place_id' />
          <div data-tooltip-id="helper">
            <InputSelectMultiple
              id='place_id'
              placeholder={t('Select place')}
              options={places}
              value={form.place_ids}
              name='place_id'
              onChange={handleChangePlaces}
              valueKey='id'
              labelKey='name'
              error={errors.place_ids}
              className={showHelper ? styles.pulseInput : null}
            />
          </div>
        </div>
        <div className={styles.formItem}>
          <Label htmlFor='invoice_number' text={<>{t('Invoice number')}<span className={styles.requiredMark}>*</span></>} />
          <Input
            id='invoice_number'
            name='invoice_number'
            placeholder={t('Enter invoice number')}
            value={form.invoice_number}
            onChange={handleChangeForm}
            error={errors.invoice_number}
            fullWidth
          />
        </div>
      </div>
      <div className={styles.formRow}>
        <div className={styles.formItem}>
          <Label text={<>{t('Invoice date')}<span className={styles.requiredMark}>*</span></>} htmlFor='invoice_date' />
          <Input
            id='invoice_date'
            name='invoice_date'
            type='date'
            placeholder={dateInputFormat}
            value={form.invoice_date}
            onChange={handleChangeForm}
            error={errors.invoice_date}
            fullWidth
          />
        </div>
        <div className={styles.formItem}>
          <Label text={t('Due date')} htmlFor='due_date' />
          <Input
            id='due_date'
            name='due_date'
            type='date'
            placeholder={dateInputFormat}
            value={form.due_date}
            onChange={handleChangeForm}
            error={errors.due_date}
            fullWidth
          />
        </div>
        <div className={styles.formItem}>
          <Label text={<>{t('Currency')}<span className={styles.requiredMark}>*</span></>} htmlFor='currency' />
          <InputSelect
            id='currency'
            placeholder={t('Select currency')}
            options={currencies}
            value={form.currency}
            name='currency'
            onChange={handleChangeForm}
            valueKey='id'
            labelKey='name'
          />
        </div>
      </div>
      <div className={styles.formRow}>
        <div className={styles.formItem}>
          <Label htmlFor='payment_terms' text={t('Payment terms')} />
          <InputSelect
            id='payment_terms'
            placeholder={t('Select payment terms')}
            options={termsOptions}
            value={form.payment_terms}
            name='payment_terms'
            onChange={handleChangeForm}
            valueKey='id'
            labelKey='title'
          />
        </div>
        <div className={styles.formItem}>
          <Label text={<>{t('Language')}<span className={styles.requiredMark}>*</span></>} htmlFor='language' />
          <InputSelect
            id='language'
            placeholder={t('Select language')}
            options={[{id: 'en', name: 'English'}, {id: 'lt', name: 'Lithuanian'}, {id: 'es', name: 'Spanish'}, {id: 'sv', name: 'Swedish'}]}
            value={form.language}
            name='language'
            onChange={handleChangeForm}
            valueKey='id'
            labelKey='name'
          />
        </div>
        <div className={styles.formItem}>
          <Label htmlFor='default_vat' text={`${t('Default VAT rate')}, %`} />
          <Input
            id='default_vat'
            name='default_vat'
            type='number'
            placeholder={t('Enter VAT rate')}
            value={form.default_vat}
            onChange={handleChangeForm}
            error={errors.default_vat}
            fullWidth
          />
        </div>
      </div>
      <div className={styles.formRow}>
        <div className={styles.formItem}>
          <Label htmlFor='notes' text={t('Customer notes')} />
          <Input
            id='notes'
            name='notes'
            placeholder={t('Enter customer notes')}
            value={form.notes}
            onChange={handleChangeForm}
            error={errors.notes}
            fullWidth
          />
        </div>
        <div className={styles.formItem}>
          <Label htmlFor='internal_notes' text={t('Internal notes (not shown to customer)')} />
          <Input
            id='internal_notes'
            name='internal_notes'
            placeholder={t('Enter internal notes')}
            value={form.internal_notes}
            onChange={handleChangeForm}
            error={errors.internal_notes}
            fullWidth
          />
        </div>
      </div>
      {
        showAssignToProject
          ? <Checkbox
              label={t('Assign invoice to project for income reporting')}
              checked={form.auto_assign_to_project}
              name='auto_assign_to_project'
              onChange={handleChangeForm} />
          : null
      }
      <Tooltip
        id="helper"
        ref={tooltipRef}
        className={styles.tooltip}
        delayShow={300}
        // isOpen={showHelper}
        noArrow />
    </div>
  )
})

export default InvoiceForm
